import React, { useEffect, useRef, useState, useMemo } from 'react'
import { useFrame } from '@react-three/fiber'
import { useGLTF, useTexture, useFBX, useAnimations } from '@react-three/drei';
import { AnimationMixer, LoopOnce } from 'three';

import createAnimation from './converter';
import blinkData from './blendDataBlink.json';
import { debug, avatarModel, avatarMovementModel, backgroundImage } from './config';

const _ = require('lodash');


function Avatar({ speechBlendData, playing }) {

  const currentClipActions = useRef(null);
  const nodeWithBlendShapes = useRef(null);
  const gltf = useGLTF(avatarModel);
  const idleFbx = useFBX(avatarMovementModel);
  
  let { clips: idleClips } = useAnimations(idleFbx.animations);
  const mixer = useMemo(() => new AnimationMixer(gltf.scene), []);

  // New blend data is available
  useEffect(() => {
    if (_.isEmpty(speechBlendData)) return;

    const clip = createAnimation(speechBlendData, nodeWithBlendShapes.current);
    const clipAction = mixer.clipAction(clip);
    clipAction.setLoop(LoopOnce);
    currentClipActions.current = clipAction;
  }, [speechBlendData]);
  

  // setPlaying is called
  useEffect(() => {
    debug('playing '+playing);
    if (playing === true) {
      if (currentClipActions.current) currentClipActions.current.play();
    } else {
      // Stop current clip actions
      if (currentClipActions.current) currentClipActions.current.stop();
    }
  }, [playing]);

    
  // Initialization
  useEffect(() => {
    // Load model
    gltf.scene.traverse(node => {
      if(node.type === 'Mesh' || node.type === 'LineSegments' || node.type === 'SkinnedMesh') {
        node.castShadow = true;
        node.receiveShadow = true;
        node.frustumCulled = false;

        if (node.morphTargetDictionary && node.name.endsWith('Head') && !nodeWithBlendShapes.current) {
          debug(`Blend shape in ${node.name} ${_.size(node.morphTargetDictionary)}`);
          nodeWithBlendShapes.current = node;
        }    
      }
    });

    // Load idle movement
    idleClips[0].tracks = _.filter(idleClips[0].tracks, track => {
      return track.name.includes("Head") || track.name.includes("Neck") || track.name.includes("Spine2");
    });

    idleClips[0].tracks = _.map(idleClips[0].tracks, track => {
      if (track.name.includes("Head")) {
        track.name = "head.quaternion";
      }

      if (track.name.includes("Neck")) {
        track.name = "neck.quaternion";
      }

      if (track.name.includes("Spine")) {
        track.name = "spine2.quaternion";
      }

      return track;
    });


    const idleClipAction = mixer.clipAction(idleClips[0]);
    idleClipAction.play();

    const blinkClip = createAnimation(blinkData, nodeWithBlendShapes.current);
    const blinkAction = mixer.clipAction(blinkClip);
    blinkAction.play();
  }, []);



  useFrame((state, delta) => {
    mixer.update(delta);
  });


  return (
    <group name="avatar">
      <primitive object={gltf.scene} dispose={null} />
    </group>
  );
}

function Bg() {
  const texture = useTexture(backgroundImage);

  return(
    <mesh position={[0, 1.5, -2]} scale={[0.8, 0.8, 0.8]}>
      <planeBufferGeometry />
      <meshBasicMaterial map={texture} />
    </mesh>
  )
}

export { Avatar, Bg };